/**
 * reference-resolver.ts
 *
 * Public URL resolver for psref:/// References.
 * Derives a human-facing URL from a Reference and an app base URL.
 *
 * Minimal and reusable — takes only baseUrl. All routing information
 * is derived from the Reference itself (kind, id, ext).
 *
 * Compatible with ReferenceResolver from @pineseed/cli/src/reference-renderer.
 */


// Structural subset of proto Reference — only the fields used here.
// Structurally compatible with the generated Reference via TypeScript structural typing.
export interface ReferenceInput {
  kind: number;
  id: string;
  ext: string;
}

// Numeric values mirror agency/reference/v1/reference.proto ReferenceKind enum.
// Declared as a const object to avoid importing the generated enum, which uses
// TypeScript enum syntax incompatible with lib/spec's erasableSyntaxOnly tsconfig.
const ReferenceKind = {
  UNSPECIFIED: 0,
  PARTICIPANT: 1,
  GROUP: 2,
  GENERAL: 3,
  URI: 4,
  FILE: 5,
  MEDIA: 6,
  CHAT_MESSAGE: 7,
  WORKSPACE: 8,
  THREAD: 9,
  CONVERSATION: 10,
} as const;
type ReferenceKindValue = (typeof ReferenceKind)[keyof typeof ReferenceKind];

// Source of truth: agency/reference/v1/reference.proto.
// Mirrors kindSlugMap in @pineseed/cli/src/reference-parser.
const KIND_TO_SLUG: Partial<Record<ReferenceKindValue, string>> = {
  [ReferenceKind.PARTICIPANT]: "participant",
  [ReferenceKind.GROUP]: "group",
  [ReferenceKind.GENERAL]: "general",
  [ReferenceKind.URI]: "uri",
  [ReferenceKind.FILE]: "file",
  [ReferenceKind.MEDIA]: "media",
  [ReferenceKind.CHAT_MESSAGE]: "chat-message",
  [ReferenceKind.WORKSPACE]: "workspace",
  [ReferenceKind.THREAD]: "thread",
  [ReferenceKind.CONVERSATION]: "conversation",
};

/**
 * Derive the public-facing URL for a Reference given an app base URL.
 *
 * Routing:
 *   URI kind with URN id  → {baseUrl}/urn/{id}
 *   all other kinds       → {baseUrl}/{kindSlug}/{id}[.{ext}]
 *
 * Throws if the kind has no slug (UNSPECIFIED) or if id is empty.
 */
export function buildPublicReferenceUrl(
  ref: ReferenceInput,
  baseUrl: string,
): string {
  if (!ref.id) throw new Error(`reference-resolver: id is required`);

  // URI kind with a URN id — routed through the /urn/ namespace.
  if (ref.kind === ReferenceKind.URI) {
    return `${baseUrl}/urn/${ref.id}`;
  }

  const slug = KIND_TO_SLUG[ref.kind as ReferenceKindValue];
  if (!slug) {
    throw new Error(`reference-resolver: no URL slug for kind ${ref.kind}`);
  }

  const suffix = ref.ext ? `.${ref.ext}` : "";
  return `${baseUrl}/${slug}/${ref.id}${suffix}`;
}

/**
 * Factory: returns a ReferenceResolver closed over baseUrl.
 * Pass the result directly as the `resolver` option wherever ReferenceResolver is accepted.
 *
 * @param baseUrl - App origin, no trailing slash (e.g. "https://app.pineseed.com")
 */
export function makeReferenceResolver(
  baseUrl: string,
): (ref: ReferenceInput) => string {
  return (ref) => buildPublicReferenceUrl(ref, baseUrl);
}
